import {
  createContext,
  useContext,
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef,
} from "react";
import { useAuth } from "./AuthContext";
import * as api from "./supabase";
import { supabase, toLocalDateStr } from "./supabase";

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const { employee, organizations } = useAuth();
  const membershipKey = organizations.map((o) => o.org_id).sort().join(",");
  return (
    <CompanyScopeProvider key={`${employee?.auth_user_id || employee?.id}:${membershipKey}`}
      employee={employee} organizations={organizations}>
      {children}
    </CompanyScopeProvider>
  );
}

function CompanyScopeProvider({ children, employee, organizations }) {
  const primaryOrg = organizations.find((o) => o.is_primary) || organizations[0];
  const [scope, setScope] = useState({ orgId: primaryOrg?.org_id, version: 0, switching: false, error: null });
  const generation = useRef(0);
  const mounted = useRef(true);
  const switchQueue = useRef(Promise.resolve());
  useLayoutEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const switchOrg = useCallback((newOrgId) => {
    if (!mounted.current) return Promise.resolve(false);
    const request = ++generation.current;
    // Invalidate before awaiting anything, including requests held by old pages.
    setScope((previous) => ({ ...previous, version: request, switching: true, error: null }));
    const current = () => mounted.current && generation.current === request;
    const operation = switchQueue.current.then(async () => {
      if (!current()) return false;
      try {
        if (!organizations.length || (newOrgId !== "all" && !organizations.some((o) => o.org_id === newOrgId))) {
          throw new Error("Choose one of your assigned companies.");
        }
        // Aggregate mode is a read scope, never a UUID or a JWT organization.
        if (newOrgId !== "all") {
          const { error } = await supabase.rpc("t4s_switch_org", { new_org_id: newOrgId });
          if (!current()) return false;
          if (error) throw error;
        }
        const { data, error } = await supabase.auth.refreshSession();
        if (!current()) return false;
        if (error) throw error;
        if (!data?.session) throw new Error("Your session could not be refreshed. Sign in again.");
        if (employee?.auth_user_id && data.session.user?.id !== employee.auth_user_id) {
          throw new Error("The signed-in account changed. Sign in again.");
        }
        if (newOrgId !== "all" && data.session.user?.app_metadata?.org_id !== newOrgId) {
          throw new Error("The refreshed session did not confirm the selected company.");
        }
        setScope({ orgId: newOrgId, version: request, switching: false, error: null });
        return true;
      } catch (error) {
        if (current()) setScope((previous) => ({ ...previous, switching: false,
          error: `Company switch was not confirmed: ${error?.message || "request failed"}. Retry before continuing.` }));
        return false;
      }
    });
    // Serialize server mutations as well as UI commits; last intent wins.
    switchQueue.current = operation.catch(() => {});
    return operation;
  }, [organizations, employee?.auth_user_id]);

  const isScopeCurrent = useCallback(() => mounted.current && generation.current === scope.version
    && !scope.switching && !scope.error, [scope]);
  return (
    <CompanyDataProvider key={`${scope.version}:${scope.orgId}:${scope.switching}`}
      employee={employee} organizations={organizations} orgId={scope.orgId}
      switchOrg={switchOrg} isScopeCurrent={isScopeCurrent}
      switchingOrg={scope.switching} orgSwitchError={scope.error}>
      {children}
    </CompanyDataProvider>
  );
}

function CompanyDataProvider({ children, employee, organizations, orgId, switchOrg,
  isScopeCurrent, switchingOrg, orgSwitchError }) {
  const mounted = useRef(true);
  const requests = useRef({});
  const readSequence = useRef(0);
  useLayoutEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; requests.current = {}; };
  }, []);
  const isCurrent = useCallback(() => mounted.current && isScopeCurrent(), [isScopeCurrent]);
  const beginRead = useCallback((key) => {
    const request = ++readSequence.current;
    requests.current[key] = request;
    return () => isCurrent() && requests.current[key] === request;
  }, [isCurrent]);
  const readCompanies = useCallback(async (reader, ...args) => {
    if (orgId !== "all") return reader(orgId, ...args);
    const results = await Promise.all(organizations.map((org) => reader(org.org_id, ...args)));
    const rows = [...new Map(results.flat().filter(Boolean).map((row) => [row.id, row])).values()];
    // Preserve the global feed order/limit after merging per-company reads.
    if (reader === api.getRecentUpdates || reader === api.getRecentJobStatuses) {
      return rows.sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")))
        .slice(0, args[0] || 20);
    }
    if (reader === api.getUpcomingEvents) {
      return rows.sort((a, b) => String(a.event_date || "").localeCompare(String(b.event_date || "")))
        .slice(0, args[0] || 10);
    }
    return rows;
  }, [orgId, organizations]);

  // Current user derived from employee
  const currentUser = employee
    ? {
        id: employee.id,
        name:
          employee.display_name ||
          `${employee.first_name} ${employee.last_name}`,
        role: employee.role || "user",
        initials: getInitials(employee),
        email: employee.email,
      }
    : null;

  // Data state
  const [jobs, setJobs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [costCodes, setCostCodes] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [recentUpdates, setRecentUpdates] = useState([]);
  const [recentJobStatuses, setRecentJobStatuses] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErrors, setLoadErrors] = useState({});
  const loadError = orgSwitchError || Object.values(loadErrors).find(Boolean) || null;

  // Initial and individual refreshes share per-resource request ordering.
  const runRead = useCallback(async (key, read, publish, retry = false, message) => {
    if (!orgId || !isCurrent()) return;
    const accepts = beginRead(key);
    for (let attempt = 0; accepts(); attempt += 1) {
      try {
        const data = await read();
        if (!accepts()) return;
        publish(data);
        setLoadErrors((previous) => ({ ...previous, [key]: null }));
        return data;
      } catch (error) {
        if (!accepts()) return;
        if (retry && attempt < 2) {
          await new Promise((resolve) => setTimeout(resolve, 1000 * (attempt + 1)));
          continue;
        }
        setLoadErrors((previous) => ({ ...previous,
          [key]: message || error?.message || "Could not load company data. Please refresh." }));
        return;
      }
    }
  }, [orgId, isCurrent, beginRead]);

  const loadJobs = useCallback(() => runRead("jobs", () => readCompanies(api.getJobs),
    (data) => setJobs(data || [])), [runRead, readCompanies]);
  const loadEmployees = useCallback(() => runRead("employees", () => readCompanies(api.getEmployees),
    (data) => setEmployees(data || [])), [runRead, readCompanies]);
  const loadCostCodes = useCallback(() => runRead("costCodes", () => readCompanies(api.getCostCodes),
    (data) => setCostCodes(data || [])), [runRead, readCompanies]);
  const loadTimeEntries = useCallback(() => runRead("timeEntries",
    () => readCompanies(api.getTimeEntries, toLocalDateStr(new Date())),
    (data) => setTimeEntries(data || [])), [runRead, readCompanies]);
  const loadOrganization = useCallback(() => runRead("organization",
    () => orgId === "all" ? Promise.resolve(null) : api.getOrganization(orgId), setOrganization),
  [runRead, orgId]);
  const readFeeds = useCallback(() => Promise.all([
    readCompanies(api.getRecentUpdates, 20), readCompanies(api.getRecentJobStatuses, 20),
  ]), [readCompanies]);
  const publishFeeds = useCallback(([updates, statuses]) => {
    setRecentUpdates(updates || []);
    setRecentJobStatuses(statuses || []);
  }, []);
  const loadRecentUpdates = useCallback(() => runRead("feeds", readFeeds, publishFeeds, false,
    "Could not refresh job updates. The last saved updates are still shown."),
  [runRead, readFeeds, publishFeeds]);
  const loadUpcomingEvents = useCallback(() => runRead("events", () => readCompanies(api.getUpcomingEvents, 10),
    (data) => setUpcomingEvents(data || [])), [runRead, readCompanies]);

  const loadInitialData = useCallback(async () => {
    if (!orgId || !isCurrent()) return;
    const accepts = beginRead("initial");
    setLoading(true);
    await Promise.all([
      runRead("jobs", () => readCompanies(api.getJobs), (data) => setJobs(data || []), true),
      runRead("employees", () => readCompanies(api.getEmployees), (data) => setEmployees(data || []), true),
      runRead("costCodes", () => readCompanies(api.getCostCodes), (data) => setCostCodes(data || []), true),
      runRead("feeds", readFeeds, publishFeeds, true),
      runRead("events", () => readCompanies(api.getUpcomingEvents), (data) => setUpcomingEvents(data || []), true),
      runRead("timeEntries", () => readCompanies(api.getTimeEntries, toLocalDateStr(new Date())),
        (data) => setTimeEntries(data || []), true),
    ]);
    if (accepts()) setLoading(false);
  }, [orgId, isCurrent, beginRead, runRead, readCompanies, readFeeds, publishFeeds]);

  useEffect(() => { loadInitialData(); }, [loadInitialData]);

  const assertCurrent = useCallback(() => {
    if (!orgId || !isCurrent()) throw new Error("Company scope changed. Reload before continuing.");
  }, [orgId, isCurrent]);

  // CRUD operations retain the initiating scope, including their post-write work.
  const createJob = useCallback(
    async (jobData) => {
      try {
        assertCurrent();
        if (orgId === "all") throw new Error("Select a company before creating a job.");
        const newJob =
          await api.createJob({ ...jobData, org_id: orgId });
        assertCurrent();
        setJobs((prev) => [...prev, newJob]);
        return newJob;
      } catch (error) {
        console.error("Error creating job:", error);
        throw error;
      }
    },
    [orgId, assertCurrent],
  );

  const updateJob = useCallback(async (jobId, updates) => {
    try {
      assertCurrent();
      const updated = await api.updateJob(jobId, updates);
      assertCurrent();
      setJobs((prev) =>
        prev.map((j) => (j.id === jobId ? { ...j, ...updated } : j)),
      );
      return updated;
    } catch (error) {
      console.error("Error updating job:", error);
      throw error;
    }
  }, [assertCurrent]);

  const createEmployee = useCallback(
    async (empData, orgAssignments = null) => {
      try {
        assertCurrent();
        if (orgId === "all") throw new Error("Select a company before creating an employee.");
        const newEmp =
          await api.createEmployee({ ...empData, org_id: orgId }, orgAssignments, employee?.auth_user_id);
        assertCurrent();
        setEmployees((prev) => [...prev, newEmp]);
        return newEmp;
      } catch (error) {
        console.error("Error creating employee:", error);
        throw error;
      }
    },
    [orgId, assertCurrent, employee?.auth_user_id],
  );

  const updateEmployee = useCallback(async (empId, updates, orgAssignments = null) => {
    try {
      assertCurrent();
      const updated = await api.updateEmployee(empId, updates, orgAssignments, employee?.auth_user_id);
      assertCurrent();
      setEmployees((prev) =>
        prev.map((e) => (e.id === empId ? { ...e, ...updated } : e)),
      );
      return updated;
    } catch (error) {
      console.error("Error updating employee:", error);
      throw error;
    }
  }, [assertCurrent, employee?.auth_user_id]);

  const refreshEmployees = loadEmployees;

  // Time entry / Payroll functions
  const getTimeEntriesForPayroll = useCallback(
    async (startDate, endDate) => {
      assertCurrent();
      try {
        const data = await readCompanies(api.getTimeEntriesByDateRange, startDate, endDate);
        assertCurrent();
        return data;
      } catch (error) {
        console.error("Error loading time entries for payroll:", error);
        throw error;
      }
    },
    [assertCurrent, readCompanies],
  );

  const approveTimeEntries = useCallback(async (entries) => {
    assertCurrent();
    if (!entries || entries.length === 0) return true;
    try {
      await api.approveTimeEntries(entries);
      assertCurrent();
      return true;
    } catch (error) {
      console.error("Error approving time entries:", error);
      throw error;
    }
  }, [assertCurrent]);

  const exportPayroll = useCallback(async (startDate, endDate, format) => {
    throw new Error("Final payroll export batches are not configured. Download a clearly labeled preview instead.");
  }, []);

  // Assignment functions
  const getAssignmentsForDateRange = useCallback(
    async (startDate, endDate) => {
      assertCurrent();
      const accepts = beginRead("assignments");
      try {
        const data = await readCompanies(api.getAssignmentsByDateRange,
          startDate,
          endDate,
        );
        assertCurrent();
        if (!accepts()) throw new Error("A newer assignment request replaced this result.");
        setAssignments(data || []);
        return data || [];
      } catch (error) {
        console.error("Error loading assignments:", error);
        throw error;
      }
    },
    [assertCurrent, beginRead, readCompanies],
  );

  const createAssignment = useCallback(
    async (assignmentData) => {
      try {
        assertCurrent();
        // When viewing "all" orgs, resolve the actual org_id from the job
        let resolvedOrgId = orgId;
        if (orgId === "all" && assignmentData.job_id) {
          const job = jobs.find((j) => j.id === assignmentData.job_id);
          resolvedOrgId = job?.org_id;
        }
        if (!resolvedOrgId || resolvedOrgId === "all") {
          throw new Error("Cannot create assignment: no valid organization selected");
        }
        const newAssignment = await api.createAssignment({
          ...assignmentData,
          org_id: resolvedOrgId,
        });
        assertCurrent();
        setAssignments((prev) => [...prev, newAssignment]);

        // Auto-show job on scheduler when someone is assigned to it
        if (assignmentData.job_id) {
          const job = jobs.find((j) => j.id === assignmentData.job_id);
          if (job && !job.is_scheduled) {
            // Legacy callers cannot make this second write atomic. A confirmed
            // assignment must not be reported as failed if only visibility fails.
            try {
              await api.updateJob(assignmentData.job_id, { is_scheduled: true });
              assertCurrent();
              setJobs((prev) =>
                prev.map((j) =>
                  j.id === assignmentData.job_id
                    ? { ...j, is_scheduled: true }
                    : j,
                ),
              );
            } catch (error) {
              if (isCurrent()) setLoadErrors((previous) => ({ ...previous,
                assignments: "Assignment saved, but job visibility could not be updated. Refresh the scheduler." }));
            }
          }
        }
        assertCurrent();
        return newAssignment;
      } catch (error) {
        console.error("Error creating assignment:", error);
        throw error;
      }
    },
    [orgId, jobs, assertCurrent, isCurrent],
  );

  const removeAssignment = useCallback(async (assignmentId) => {
    try {
      assertCurrent();
      await api.deleteAssignment(assignmentId);
      assertCurrent();
      setAssignments((prev) => prev.filter((a) => a.id !== assignmentId));
    } catch (error) {
      console.error("Error removing assignment:", error);
      throw error;
    }
  }, [assertCurrent]);

  // Time entry functions
  const getTimeEntriesForDate = useCallback(
    async (date, jobId = null) => {
      assertCurrent();
      try {
        const data = await readCompanies(api.getTimeEntries, date, jobId);
        assertCurrent();
        return data || [];
      } catch (error) {
        console.error("Error loading time entries:", error);
        throw error;
      }
    },
    [assertCurrent, readCompanies],
  );

  const value = {
    // Auth-derived values
    orgId,
    selectedOrgId: orgId,
    switchingOrg,
    orgSwitchError,
    currentUser,
    organizations,
    organization,
    availableOrgs: organizations,
    setSelectedOrgId: switchOrg,

    // Data
    jobs,
    employees,
    costCodes,
    assignments,
    timeEntries,
    loading,
    loadError,
    jobsLoading: loading,
    timeEntriesLoading: loading,

    // For components expecting these
    selectedDate: toLocalDateStr(new Date()),
    recentUpdates,
    recentJobStatuses,
    upcomingEvents,
    loadRecentUpdates,
    loadUpcomingEvents,

    // Loaders
    loadJobs,
    loadEmployees,
    loadCostCodes,
    loadTimeEntries,
    loadOrganization,
    refreshEmployees,
    refreshData: loadInitialData,

    // CRUD
    createJob,
    updateJob,
    createEmployee,
    updateEmployee,

    // Assignments
    getAssignmentsForDateRange,
    createAssignment,
    removeAssignment,

    // Time entries / Payroll
    getTimeEntriesForDate,
    getTimeEntriesForPayroll,
    approveTimeEntries,
    exportPayroll,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useApp must be used within AppProvider");
  }
  return context;
}

// Helper function
function getInitials(employee) {
  if (!employee) return "??";
  const first = employee.first_name?.[0] || "";
  const last = employee.last_name?.[0] || "";
  return (first + last).toUpperCase() || "??";
}
